import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CircularProgress } from '@getgo/chameleon-web-react-wrapper';

import { useAppDispatch, useAppSelector } from 'hooks';
import { RazorpayWrapper } from 'lib/razorpay-checkout';
import { sessionBillingAddress } from 'modules/session-details';
import { cancelRazorpayTransaction } from 'modules/transaction-razorpay/transaction-razorpay-slice';
import { AddAndPayRazorpayStep1Resp, AddRazorpayStep1Resp, AddRazorpayStep2Payload } from 'types/transaction-razorpay';
import { GOTO_INDIA_ENTITY } from 'utils/constants';
import st from 'utils/shared-translations';

import './razorpay-common.css';

interface RazorpayCommonProps {
  Razorpay: RazorpayWrapper | undefined;
  step1Resp: AddRazorpayStep1Resp | AddAndPayRazorpayStep1Resp;
  handlePaymentSuccess: (data: AddRazorpayStep2Payload) => void;
  handleModalClose: () => void;
}

const RazorpayCommon: FC<RazorpayCommonProps> = ({
  Razorpay,
  step1Resp,
  handlePaymentSuccess,
  handleModalClose,
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const isRazorpayInitialized = useRef<boolean>(false);
  const [loadingMsg, setLoadingMsg] = useState<keyof typeof st>('razorpay.add.loading.first.msg');

  const selectedBillingAddress = useAppSelector(sessionBillingAddress);

  const handleFailure = (resp) => {
    const payload = {
      code: resp.error.code,
      description: resp.error.description,
      source: resp.error.source,
      step: resp.error.step,
      reason: resp.error.reason,
      orderId: resp.error.metadata.order_id,
      paymentId: resp.error.metadata.payment_id,
      paymentMethodKey: step1Resp.paymentMethodKey,
    };
    dispatch(cancelRazorpayTransaction(payload));
  };

  const handleSuccess = (resp) => {
    setLoadingMsg('razorpay.add.loading.second.msg');
    const data: AddRazorpayStep2Payload = {
      customerId: step1Resp.customerId,
      orderId: step1Resp.orderId,
      paymentId: resp.razorpay_payment_id,
      signature: resp.razorpay_signature,
      paymentMethodKey: step1Resp.paymentMethodKey,
    };
    handlePaymentSuccess(data);
  };

  if (!isRazorpayInitialized.current && step1Resp.customerId) {
    isRazorpayInitialized.current = true;
    const options = {
      key: step1Resp.key,
      name: GOTO_INDIA_ENTITY,
      order_id: step1Resp.orderId,
      customer_id: step1Resp.customerId,
      recurring: step1Resp.recurring,
      prefill: {
        name: selectedBillingAddress.firstName + ' ' + selectedBillingAddress.lastName,
        email: selectedBillingAddress.email,
        contact: `91${selectedBillingAddress.phone}`,
      },
      readonly: {
        contact: false,
        email: true,
        name: true,
      },
      notes: {
        note_key_1: step1Resp.noteKey1,
        note_key_2: step1Resp.noteKey2,
      },
      theme: {
        color: '#0f3599',
      },
      handler: handleSuccess,
      modal: {
        ondismiss: handleModalClose,
        escape: false,
        backdropclose: false,
      },
    };
    Razorpay?.initialize(options).then(() =>
      Razorpay?.addEventListener('payment.failed', (resp) => handleFailure(resp)),
    );
  }

  return (
    <div className="razorpay-common">
      <CircularProgress />
      <span>{intl.formatMessage(st[loadingMsg])}</span>
    </div>
  );
};

export default RazorpayCommon;
